import _get from 'lodash/get';
import { TFunction } from 'i18next';
import {
  AiDocumentationFieldEnum,
  Documentation,
  EdmDocumentRenewalReminderTemplateFragmentFragment,
} from 'graphql-common';
import { FieldItem } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import { AttachmentValue } from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import getAttachmentMeta from '@lib/components/DocumentationPreview/utils/getAttachmentMeta';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import {
  getAttachmentValidation,
  getCodeValidation,
  getFieldValidation,
  getOptionsArrayValidation,
} from '@lib/utils/validationUtils';
import { GetLoadOptions } from '@lib/components/Select/types';
import TagsInput, { TagsInputProps } from '@lib/components/TagsInput/TagsInput';
import { OnCreateTagFn } from '@lib/components/TagsInput/types';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import {
  FileSizeLimits,
  UppyDocumentationImportAllowedFileTypes,
  videoSizeLimit,
} from '@lib/enums/mimeTypes';
import { ButtonSizes } from '@lib/components/Button/Button';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import getAiFieldDetectionState from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from '../components/AiFieldAssist';
import AiAutofillRow from '../components/AiAutofillRow';
import getCategoryFields from './getCategoryFields';
import getAccessValidationFields from './getAccessValidationFields';
import getOperationalScopeFields from './getOperationalScopeFields';
import getLifecycleFields from './getLifecycleFields';

type Args = {
  t: TFunction<'translation', undefined>;
  isEdit?: boolean;
  approverLoadOptions: GetLoadOptions;
  areasLoadOptions: GetLoadOptions;
  categoriesLoadOptions: GetLoadOptions;
  equipmentLoadOptions: GetLoadOptions;
  formTemplatesLoadOptions: GetLoadOptions;
  onCreateTag: OnCreateTagFn;
  sectorsLoadOptions: GetLoadOptions;
  sitesLoadOptions: GetLoadOptions;
  tagsLoadOptions: GetLoadOptions;
  tasksLoadOptions: GetLoadOptions;
  openEquipmentModal?: () => void;
  openAreasModal?: () => void;
  openFormTemplatesModal?: () => void;
  openTasksModal?: () => void;
  openAddCategoryModal?: () => void;
  categoryCacheKey?: number;
  renewalReminderTemplate?: EdmDocumentRenewalReminderTemplateFragmentFragment | null;
  hasPriorReminderValues?: boolean;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasCategoriesAccess?: boolean;
  hasSitesAccess?: boolean;
  hasAssetsAccess?: boolean;
  hasTasksAccess?: boolean;
  hasUsersAccess?: boolean;
  // AI auto-fill is available only when the company plan grants AiAssistance
  // (Write). In edit mode the saved version is the generation source; in create
  // mode it's the freshly-uploaded attachment (resolved from the form).
  isAiEnabled?: boolean;
  // Whether the company's AI indexing pipeline can index video (region-derived,
  // via CompanyPlan.aiIndexableMediaKinds). Some regions can't index video even
  // when AiAssistance itself is enabled, so a video attachment must still hide
  // the assist affordance.
  isVideoAiIndexingSupported?: boolean;
  documentationVersionId?: string;
  // Remaining AI credits shown under each assist row; null/undefined hides it.
  aiCredits?: AiCredits | null;
  // Refetch the AI budget after a generation consumes credits.
  // The saved document's persisted AI-detection state — undefined in create
  // mode or when detection data isn't loaded (mirrors the view page's `doc`).
  // Includes code/description/tags (not just the detection fields) because
  // `getAiFieldDetectionState`'s "a present value wins over a stale status"
  // rule reads them.
  doc?: Pick<
    Documentation,
    | 'name'
    | 'code'
    | 'description'
    | 'tags'
    | 'category'
    | 'aiDetectionStatus'
    | 'suggestedCodes'
  >;
  isFieldDismissed?: (field: AiDocumentationFieldEnum) => boolean;
  onDismissField?: (field: AiDocumentationFieldEnum) => void;
};

export default function getDocumentFormFields({
  t,
  isEdit,
  approverLoadOptions,
  areasLoadOptions,
  categoriesLoadOptions,
  equipmentLoadOptions,
  formTemplatesLoadOptions,
  onCreateTag,
  sectorsLoadOptions,
  sitesLoadOptions,
  tagsLoadOptions,
  tasksLoadOptions,
  openEquipmentModal,
  openAreasModal,
  openFormTemplatesModal,
  openTasksModal,
  openAddCategoryModal,
  categoryCacheKey,
  renewalReminderTemplate,
  hasPriorReminderValues,
  isFullAdmin = true,
  isPlanLocked,
  hasCategoriesAccess = true,
  hasSitesAccess = true,
  hasAssetsAccess = true,
  hasTasksAccess = true,
  hasUsersAccess = true,
  isAiEnabled = false,
  isVideoAiIndexingSupported = true,
  documentationVersionId,
  aiCredits,
  doc,
  isFieldDismissed,
  onDismissField,
}: Args): FieldItem[] {
  // A video attachment whose plan region can't index video has nothing for AI
  // to read, so the assist affordance stays hidden regardless of isAiEnabled.
  const isVideoAttachmentUnsupported = (values: Record<string, unknown>) =>
    !isVideoAiIndexingSupported &&
    getAttachmentMeta(
      _get(values, [DocumentFormFieldNames.Attachment]) as AttachmentValue,
    ).mimeType.startsWith('video');

  // The AI affordance is hidden until a generation source exists: a saved
  // version (edit) or an uploaded attachment (create).
  const isAiAssistVisible = (values: Record<string, unknown>) =>
    isAiEnabled &&
    (isEdit
      ? !!documentationVersionId
      : !!_get(values, [DocumentFormFieldNames.Attachment, 'file', 'id'])) &&
    !isVideoAttachmentUnsupported(values);

  const detectionState = (field: AiDocumentationFieldEnum) =>
    getAiFieldDetectionState({
      doc,
      field,
      isDismissed: isFieldDismissed?.(field) ?? false,
    });
  const onDismiss = (field: AiDocumentationFieldEnum) => () =>
    onDismissField?.(field);

  return [
    // ── Basic information ─────────────────────────────────────────
    {
      name: DocumentFormFieldNames.BasicInformationTitle,
      customRender: () => (
        <FieldTitleRender
          key="basicInformation"
          title={t('basic-information')}
          isBlockStart
        />
      ),
    },
    {
      name: DocumentFormFieldNames.Attachment,
      label: '',
      validation: !isEdit
        ? getAttachmentValidation({ required: true, t })
        : undefined,
      element: FileUploader,
      componentProps: {
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDocumentationImportAllowedFileTypes,
        note: t('documents-import-files-upload-note', {
          sizeImg: FileSizeLimits.Image,
          sizePdf: FileSizeLimits.File,
          sizeVideo: FileSizeLimits.Video,
        }),
        maxFileSize: videoSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-files'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
        disabled: isEdit,
      },
    },
    {
      name: DocumentFormFieldNames.AiAutofillRow,
      isVisibleFn: isAiAssistVisible,
      customRender: () => (
        <AiAutofillRow
          key="aiAutofillRow"
          isEdit={isEdit}
          documentationVersionId={documentationVersionId}
          credits={aiCredits}
        />
      ),
    },
    {
      name: DocumentFormFieldNames.Name,
      label: t('document-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
      getComponentProps: (values) =>
        isAiAssistVisible(values)
          ? {
              rightAdornment: (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Name}
                  targetFieldName={DocumentFormFieldNames.Name}
                  isEdit={isEdit}
                  documentationVersionId={documentationVersionId}
                  creditsRemaining={aiCredits}
                  detectionUiState={
                    detectionState(AiDocumentationFieldEnum.Name).uiState
                  }
                  detectingLabel={t('ai-generating-name')}
                  onDismiss={onDismiss(AiDocumentationFieldEnum.Name)}
                  labels={{
                    idleLabel: t('ai-generate-with-ai'),
                    idleCta: t('ai-generate-name-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              ),
            }
          : undefined,
    },
    ...getCategoryFields({
      t,
      categoriesLoadOptions,
      isFullAdmin,
      isPlanLocked,
      hasCategoriesAccess,
      openAddCategoryModal,
      categoryCacheKey,
      isAiAssistVisible,
      isEdit,
      documentationVersionId,
      aiCredits,
      detectionUiState: detectionState(AiDocumentationFieldEnum.Category)
        .uiState,
      onDismiss: onDismiss(AiDocumentationFieldEnum.Category),
    }),
    {
      name: DocumentFormFieldNames.Description,
      label: t('description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
      getComponentProps: (values) =>
        isAiAssistVisible(values)
          ? {
              toolbarExtra: (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Description}
                  targetFieldName={DocumentFormFieldNames.Description}
                  isEdit={isEdit}
                  documentationVersionId={documentationVersionId}
                  creditsRemaining={aiCredits}
                  detectionUiState={
                    detectionState(AiDocumentationFieldEnum.Description).uiState
                  }
                  detectingLabel={t('ai-generating-description')}
                  onDismiss={onDismiss(AiDocumentationFieldEnum.Description)}
                  labels={{
                    idleLabel: t('ai-generate-with-ai'),
                    idleCta: t('ai-generate-description-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              ),
            }
          : undefined,
    },
    // ── Document identification ───────────────────────────────────
    {
      name: DocumentFormFieldNames.DocumentIdentificationTitle,
      customRender: () => (
        <FieldTitleRender
          key="documentIdentification"
          title={t('document-identification')}
          isBlockStart
        />
      ),
    },
    {
      name: DocumentFormFieldNames.Code,
      label: t('code'),
      element: Input,
      validation: getCodeValidation({ t, max: 255, allowHyphen: true }),
      getComponentProps: (values) =>
        isAiAssistVisible(values)
          ? {
              rightAdornment: (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Code}
                  targetFieldName={DocumentFormFieldNames.Code}
                  isEdit={isEdit}
                  documentationVersionId={documentationVersionId}
                  creditsRemaining={aiCredits}
                  detectionUiState={
                    detectionState(AiDocumentationFieldEnum.Code).uiState
                  }
                  suggestedCodes={doc?.suggestedCodes ?? undefined}
                  detectingLabel={t('ai-detecting-code')}
                  onDismiss={onDismiss(AiDocumentationFieldEnum.Code)}
                  labels={{
                    idleLabel: t('ai-detect-with-ai'),
                    idleCta: t('ai-detect-code-cta'),
                    regenerateCta: t('ai-detect-code-again'),
                  }}
                />
              ),
            }
          : undefined,
    },
    {
      name: DocumentFormFieldNames.Tags,
      label: t('tags'),
      element: TagsInput,
      componentProps: {
        tagsLoadOptions,
        onCreateTag,
        hasErrorAsTooltip: true,
        tagsRowHasBottomSpace: true,
      } as TagsInputProps,
      getComponentProps: (values) =>
        isAiAssistVisible(values)
          ? {
              rightAdornment: (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Tags}
                  targetFieldName={DocumentFormFieldNames.Tags}
                  onCreateTag={onCreateTag}
                  isEdit={isEdit}
                  documentationVersionId={documentationVersionId}
                  creditsRemaining={aiCredits}
                  detectionUiState={
                    detectionState(AiDocumentationFieldEnum.Tags).uiState
                  }
                  detectingLabel={t('ai-generating-tags')}
                  onDismiss={onDismiss(AiDocumentationFieldEnum.Tags)}
                  labels={{
                    idleLabel: t('ai-suggest-with-ai'),
                    idleCta: t('ai-suggest-tags-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              ),
            }
          : undefined,
      validation: getOptionsArrayValidation({ t }),
      hideErrors: true,
    },
    // ── Access & validation ───────────────────────────────────────
    ...getAccessValidationFields({
      t,
      sectorsLoadOptions,
      sitesLoadOptions,
      approverLoadOptions,
      isFullAdmin,
      isPlanLocked,
      hasSitesAccess,
      hasUsersAccess,
    }),
    // ── Operational scope ─────────────────────────────────────────
    ...getOperationalScopeFields({
      t,
      equipmentLoadOptions,
      areasLoadOptions,
      formTemplatesLoadOptions,
      tasksLoadOptions,
      openEquipmentModal,
      openAreasModal,
      openFormTemplatesModal,
      openTasksModal,
      isFullAdmin,
      isPlanLocked,
      hasAssetsAccess,
      hasSitesAccess,
      hasTasksAccess,
    }),
    // ── Lifecycle ─────────────────────────────────────────────────
    ...getLifecycleFields({
      t,
      template: renewalReminderTemplate,
      hasPriorReminderValues,
      isFullAdmin,
      isPlanLocked,
    }),
  ];
}
